import {Link, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {Empty, Tag} from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/vi';
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {useCan} from "~/hooks";
import {currentUserSelector} from "~/reduxs/Auth/authSlice";
import {useGetProjectsSummaryQuery} from "~/reduxs/api/projectApiSlice";
import AccountPlanCard from "~/features/Account/components/AccountPlanCard";
import style from '../style/Home.module.scss'

dayjs.locale('vi');

const cn = className.bind(style);

/** Chip trạng thái crawl mới nhất của 1 dự án. */
function CrawlChip({crawl}) {
	if (!crawl) return <Tag>Chưa crawl</Tag>;
	if (crawl.status === 'done') {
		return <Tag color={crawl.grade_color || 'default'}>Điểm {crawl.score}/100 · {crawl.pages} trang</Tag>;
	}
	if (crawl.status === 'running' || crawl.status === 'queued') {
		return <Tag color="processing">Đang crawl…</Tag>;
	}
	return <Tag color="red">Crawl lỗi</Tag>;
}

/** Chip trạng thái kết nối Google Search Console. */
function GscChip({gsc}) {
	if (gsc?.connected) return <Tag color="green">GSC đã kết nối</Tag>;
	if (gsc?.status === 'property_pending') return <Tag color="orange">GSC: chưa chọn property</Tag>;
	return <Tag>GSC chưa kết nối</Tag>;
}

/**
 * Dashboard — trang chủ (v1).
 * Chào người dùng + quick action theo quyền + danh sách dự án kèm trạng thái tool
 * (crawl mới nhất, kết nối GSC — API projects/summary) + card Gói & Hạn mức.
 */
function Home() {
	const navigate = useNavigate();
	const currentUser = useSelector(currentUserSelector);

	const canSeo = useCan('seo');
	const canPageSpeed = useCan('pagespeed');
	const canSchema = useCan('schema');
	const canAi = useCan('ai');
	const canProject = useCan('project');
	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');

	// Cùng điều kiện hiển thị mục "Dự án" như sidebar (NavBarData).
	const showProjects = canProject || canCrawl || canGsc || canAi;

	const {data: projects = [], isLoading} = useGetProjectsSummaryQuery(undefined, {skip: !showProjects});

	const quickActions = [
		showProjects && {to: '/projects', icon: 'fa-light fa-folder-tree', tone: 'primary', label: 'Dự án', desc: 'Quản lý website & công cụ'},
		canSeo && {to: '/seo', icon: 'fa-light fa-magnifying-glass-chart', tone: 'success', label: 'Chấm điểm SEO', desc: 'Kiểm tra 1 trang bất kỳ'},
		canPageSpeed && {to: '/pagespeed', icon: 'fa-light fa-gauge-high', tone: 'warning', label: 'PageSpeed', desc: 'Đo Core Web Vitals'},
		canSchema && {to: '/schema/generator', icon: 'fa-light fa-shapes', tone: 'purple', label: 'Schema.org', desc: 'Tạo & kiểm tra schema'},
		canAi && {to: '/ai', icon: 'fa-light fa-wand-magic-sparkles', tone: 'orange', label: 'AI SEO', desc: 'Sinh nội dung bằng AI'},
	].filter(Boolean);

	return (
		<div className="container">
			<div className={cn('dashboard')}>
				<div className={cn('dashboardHeader')}>
					<div>
						<h1 className={cn('dashboardTitle')}>
							Xin chào, {currentUser?.lastname} {currentUser?.firstname}
						</h1>
						<div className={cn('dashboardSub')}>
							{dayjs().format('dddd, DD/MM/YYYY')} — bắt đầu với một công cụ bên dưới.
						</div>
					</div>
					{showProjects && (
						<Button primary onClick={() => navigate('/projects')}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
							Tạo dự án
						</Button>
					)}
				</div>

				{quickActions.length > 0 && (
					<div className={cn('quickGrid')}>
						{quickActions.map((a) => (
							<Link key={a.to} to={a.to} className={cn('statCard', 'statCardLink')}>
								<div className={cn('statTop')}>
									<span className={cn('statIcon', a.tone)}><FontAwesomeIcon icon={a.icon} /></span>
									<div className={cn('statMain')}>
										<div className={cn('quickLabel')}>{a.label}</div>
										<div className={cn('statLabel')}>{a.desc}</div>
									</div>
								</div>
							</Link>
						))}
					</div>
				)}

				{showProjects && (
					<div className={cn('card')} style={{marginBottom: 20}}>
						<div className={cn('cardHead')}>
							<span className={cn('cardTitle')}>
								<FontAwesomeIcon icon="fa-light fa-folder-tree" /> Dự án của bạn
							</span>
							<Link to="/projects" className={cn('cardLink')}>
								Tất cả dự án <FontAwesomeIcon icon="fa-light fa-arrow-right" />
							</Link>
						</div>

						{isLoading ? (
							<Loading />
						) : projects.length === 0 ? (
							<div className={cn('emptyBox')}>
								<Empty description="Chưa có dự án nào. Tạo dự án đầu tiên để crawl website, kết nối Search Console…">
									<Button primary onClick={() => navigate('/projects')}
										leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
										Tạo dự án
									</Button>
								</Empty>
							</div>
						) : (
							<div className={cn('actionList')}>
								{projects.map((p) => (
									<div key={p.id} className={cn('actionItem', 'projectRow')}
										onClick={() => navigate(`/projects/${p.id}`)}>
										<span className={cn('feedIcon', 'primary')}>
											<FontAwesomeIcon icon="fa-light fa-globe" />
										</span>
										<div className={cn('actionInfo')}>
											<div className={cn('actionName')}>{p.name}</div>
											<div className={cn('actionSub')}>
												{p.domain || p.root_url}
												{p.crawl?.finished_at ? ` · crawl gần nhất ${dayjs(p.crawl.finished_at).format('DD/MM/YYYY')}` : ''}
											</div>
										</div>
										<div className={cn('projectChips')}>
											<CrawlChip crawl={p.crawl} />
											<GscChip gsc={p.gsc} />
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				)}

				<AccountPlanCard />
			</div>
		</div>
	);
}

export default Home;
